import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { Logo, Wordmark } from '@/components/ui/Logo';
import { useAuth } from './AuthProvider';

export function SignInPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [challenge, setChallenge] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  if (auth.status === 'signedIn') return <Navigate to="/app" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      if (challenge) {
        if (newPassword !== confirmPassword) {
          setError(t('auth.passwordsDiffer'));
          return;
        }
        await auth.completeNewPassword(newPassword);
        navigate('/app', { replace: true });
        return;
      }
      const res = await auth.signIn(email, password);
      if (!res.done) setChallenge(true);
      else navigate('/app', { replace: true });
    } catch {
      // Deliberately generic: never reveal whether the account exists.
      setError(t('auth.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-4 py-12">
      <div className="mb-8 flex items-center gap-3">
        <Logo size={40} tone="leaf" />
        <Wordmark className="text-leaf" />
      </div>

      <h1 className="font-display text-display">
        {challenge ? t('auth.setPasswordTitle') : t('auth.welcome')}
      </h1>
      <p className="mt-2 text-body text-leaf-soft">
        {challenge ? t('auth.setPasswordBody') : t('auth.welcomeBody')}
      </p>

      <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4" noValidate>
        {!challenge ? (
          <>
            <Field label={t('auth.email')}>
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('auth.password')}>
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
          </>
        ) : (
          <>
            <Field label={t('auth.newPassword')} hint="At least 8 characters.">
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('auth.confirmNewPassword')}>
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              )}
            </Field>
          </>
        )}

        {error && (
          <p role="alert" className="text-body text-status-declinedFg">
            {error}
          </p>
        )}

        <Button type="submit" loading={busy}>
          {busy ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
      </form>

      <p className="mt-8 text-caption text-leaf-soft">
        {t('auth.invited')}{' '}
        <Link to="/" className="text-brass-text underline underline-offset-4">
          {t('landing.joinWaitlist')}
        </Link>
      </p>
    </main>
  );
}
